const maxWidth = {
    name: "max-width",
    path: "/max-width",
    title: "max-width",
    description: "Utilities for controlling the maximum width of elements.",
    value: {
        "max-width": {
            "max-w-<number>": "calc(var(--1) * <number>)",
            "max-w-<fraction>": "calc(<fraction> * 100%)",
            "max-w-3xs": "var(--container-3xs); /* 16rem (256px) */",
            "max-w-2xs": "var(--container-2xs); /* 18rem (288px) */",
            "max-w-xs": "var(--container-xs); /* 20rem (320px) */",
            "max-w-sm": "var(--container-sm); /* 24rem (384px) */",
            "max-w-md": "var(--container-md); /* 28rem (448px) */",
            "max-w-lg": "var(--container-lg); /* 32rem (512px) */",
            "max-w-xl": "var(--container-xl); /* 36rem (576px) */",
            "max-w-2xl": "var(--container-2xl); /* 42rem (672px) */",
            "max-w-3xl": "var(--container-3xl); /* 48rem (768px) */",
            "max-w-4xl": "var(--container-4xl); /* 56rem (896px) */",
            "max-w-5xl": "var(--container-5xl); /* 64rem (1024px) */",
            "max-w-6xl": "var(--container-6xl); /* 72rem (1152px) */",
            "max-w-7xl": "var(--container-7xl); /* 80rem (1280px) */",
            "max-w-auto": "auto",
            "max-w-px": "1px",
            "max-w-full": "100%",
            "max-w-screen": "100vw",
            "max-w-dvw": "100dvw",
            "max-w-dvh": "100dvh",
            "max-w-lvw": "100lvw",
            "max-w-lvh": "100lvh",
            "max-w-svw": "100svw",
            "max-w-svh": "100svh",
            "max-w-min": "min-content",
            "max-w-max": "max-content",
            "max-w-fit": "fit-content",
            "max-w-[<value>]": "<value>",
        },
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="max-w-96 bg-color-blue-500 rounded-lg p-2 text-center">max-w-96</div>
<div class="max-w-80 bg-color-blue-500 rounded-lg p-2 text-center">max-w-80</div>
<div class="max-w-64 bg-color-blue-500 rounded-lg p-2 text-center">max-w-64</div>
<div class="max-w-48 bg-color-blue-500 rounded-lg p-2 text-center">max-w-48</div>
<div class="max-w-40 bg-color-blue-500 rounded-lg p-2 text-center">max-w-40</div>
<div class="max-w-32 bg-color-blue-500 rounded-lg p-2 text-center">max-w-32</div>
<div class="max-w-24 bg-color-blue-500 rounded-lg p-2 text-center">max-w-24</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="flex gap-4 text-color-white font-mono font-bold">
  <div class="max-w-1/2 bg-color-blue-500 rounded-lg p-2 text-center">max-w-1/2</div>
  <div class="max-w-1/2 bg-color-blue-500 rounded-lg p-2 text-center">max-w-1/2</div>
</div>
<div class="flex gap-4 text-color-white font-mono font-bold">
  <div class="max-w-2/5 bg-color-blue-500 rounded-lg p-2 text-center">max-w-2/5</div>
  <div class="max-w-3/5 bg-color-blue-500 rounded-lg p-2 text-center">max-w-3/5</div>
</div>
<div class="flex gap-4 text-color-white font-mono font-bold">
  <div class="max-w-1/3 bg-color-blue-500 rounded-lg p-2 text-center">max-w-1/3</div>
  <div class="max-w-2/3 bg-color-blue-500 rounded-lg p-2 text-center">max-w-2/3</div>
</div>
<div class="flex gap-4 text-color-white font-mono font-bold">
  <div class="max-w-1/4 bg-color-blue-500 rounded-lg p-2 text-center">max-w-1/4</div>
  <div class="max-w-3/4 bg-color-blue-500 rounded-lg p-2 text-center">max-w-3/4</div>
</div>
<div class="flex gap-4 text-color-white font-mono font-bold">
  <div class="max-w-1/5 bg-color-blue-500 rounded-lg p-2 text-center">max-w-1/5</div>
  <div class="max-w-4/5 bg-color-blue-500 rounded-lg p-2 text-center">max-w-4/5</div>
</div>
<div class="flex gap-4 text-color-white font-mono font-bold">
  <div class="max-w-1/6 bg-color-blue-500 rounded-lg p-2 text-center">max-w-1/6</div>
  <div class="max-w-5/6 bg-color-blue-500 rounded-lg p-2 text-center">max-w-5/6</div>
</div>
<div class="max-w-full bg-color-blue-500 rounded-lg p-2 text-center">max-w-full</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="max-w-xl bg-color-blue-500 rounded-lg p-2 text-center">max-w-xl</div>
<div class="max-w-lg bg-color-blue-500 rounded-lg p-2 text-center">max-w-lg</div>
<div class="max-w-md bg-color-blue-500 rounded-lg p-2 text-center">max-w-md</div>
<div class="max-w-sm bg-color-blue-500 rounded-lg p-2 text-center">max-w-sm</div>
<div class="max-w-xs bg-color-blue-500 rounded-lg p-2 text-center">max-w-xs</div>
<div class="max-w-2xs bg-color-blue-500 rounded-lg p-2 text-center">max-w-2xs</div>
<div class="max-w-3xs bg-color-blue-500 rounded-lg p-2 text-center">max-w-3xs</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="size-[64px] grid place-content-center place-items-center bg-color-blue-500 rounded-lg p-2 text-center">size-[64px]</div>
<div class="size-20 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center">size-20</div>
<div class="size-24 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center">size-24</div>
<div class="size-32 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center">size-32</div>
<div class="size-40 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center">size-40</div>
                `
            }
        }
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">max-w-&lt;number&gt;</code> utilities to set fixed maximum width values:
            </p>
            <div class="space-y-4 text-color-white font-mono font-bold relative">
                <div class="absolute inset-0 z-0 !mb-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="relative max-w-96 bg-color-blue-500 rounded-lg p-2 text-center">max-w-96</div>
                <div class="relative max-w-80 bg-color-blue-500 rounded-lg p-2 text-center">max-w-80</div>
                <div class="relative max-w-64 bg-color-blue-500 rounded-lg p-2 text-center">max-w-64</div>
                <div class="relative max-w-48 bg-color-blue-500 rounded-lg p-2 text-center">max-w-48</div>
                <div class="relative max-w-40 bg-color-blue-500 rounded-lg p-2 text-center">max-w-40</div>
                <div class="relative max-w-32 bg-color-blue-500 rounded-lg p-2 text-center">max-w-32</div>
                <div class="relative max-w-24 bg-color-blue-500 rounded-lg p-2 text-center">max-w-24</div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">max-w-full, max-w-&lt;fraction&gt;</code>
                utilities like max-w-1/2 and max-w-2/5 to give an element a percentage-based maximum width:
            </p>
            <div>
                <div class="gap-4 text-color-white font-mono font-bold relative w-full grid">
                    <div class="relative">
                        <div class="max-w-9/10 relative z-10 bg-color-blue-500 rounded-lg p-2 text-center">max-w-9/10</div>
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                    </div>
                    <div class="relative">
                        <div class="max-w-3/4 relative z-10 bg-color-blue-500 rounded-lg p-2 text-center">max-w-3/4</div>
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                    </div>
                    <div class="relative">
                        <div class="max-w-1/2 relative z-10 bg-color-blue-500 rounded-lg p-2 text-center">max-w-1/2</div>
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                    </div>
                    <div class="relative">
                        <div class="max-w-1/3 relative z-10 bg-color-blue-500 rounded-lg p-2 text-center">max-w-1/3</div>
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                    </div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                <code class="text-color-gray-800 dark:text-color-white font-semibold">Using the container scale</code>
                Use utilities like max-w-sm and max-w-xl to set an element to a fixed maximum width based on the container scale:
            </p>
            <div class="space-y-4 text-color-white font-mono font-bold">
                <div class="max-w-xl bg-color-blue-500 rounded-lg p-2 text-center">max-w-xl</div>
                <div class="max-w-lg bg-color-blue-500 rounded-lg p-2 text-center">max-w-lg</div>
                <div class="max-w-md bg-color-blue-500 rounded-lg p-2 text-center">max-w-md</div>
                <div class="max-w-sm bg-color-blue-500 rounded-lg p-2 text-center">max-w-sm</div>
                <div class="max-w-xs bg-color-blue-500 rounded-lg p-2 text-center">max-w-xs</div>
                <div class="max-w-2xs bg-color-blue-500 rounded-lg p-2 text-center">max-w-2xs</div>
                <div class="max-w-3xs bg-color-blue-500 rounded-lg p-2 text-center">max-w-3xs</div>
            </div>
            `
        },
    ]
};
export default maxWidth;

<div className="sr-only max-w-xl max-w-lg max-w-3/4 max-w-1/2 max-w-1/3  max-w-9/10 min-w-xl max-w-[100px] max-w-lg max-w-md max-w-sm max-w-xs max-w-2xs max-w-3xs  max-w-full max-w-3/4 w-full !mb-0 space-y-4 w-60 w-20 max-w-96 max-w-80 max-w-64 max-w-48 max-w-40 max-w-32 max-w-24">

</div>